import { useLayoutEffect, useRef, useState, useContext } from "react";
import { ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import TypingIndicator from "./TypingIndicator";
import HumanTyping from "./ui/HumanTyping";
import { useScrollZones } from "../hooks/useScrollZones";
import { AuthContext } from "../context/AuthContext";


export default function MessageFeed({ messages, isAwaitingResponse, chatId, expert, gotoJobPage, enableChat, onSuggestionClick }) {
  const { isLogin, setIsLogin } = useContext(AuthContext);
  const containerRef = useRef(null);
  const spacerRef = useRef(null);
  const introRef = useRef(null);
  const itemRefs     = useRef({});
  const [firstLoad, setFirstLoad] = useState(() => {
    if ( isLogin === true )
      return true
    else
      return messages.length >= 1
  });
  const zone = useScrollZones(containerRef);

  function sentenceDone() {
    enableChat()
    scrollToNeeded(false)
    setFirstLoad(false)
    setIsLogin(null)
  }

  function scrollToNeeded() {
    const lastUser   = [...messages].reverse().find((m) => m.from === "user");
    const lastBot   = [...messages].reverse().find((m) => m.from !== "user");
    const lastMessage   = messages[messages.length - 1];
    const penultimateMessage   = messages[messages.length - 2];

    if ( !lastUser || !lastBot ) return;

    const userRef = itemRefs.current[lastUser.id].el
    const botRef = itemRefs.current[lastBot.id].el
    const penultimateRef = itemRefs.current[penultimateMessage.id].el
    let available = containerRef.current.clientHeight - userRef.offsetHeight;

    if ( botRef && lastMessage.from !== "user")
      available = available - botRef.offsetHeight - 48

    if ( penultimateRef && penultimateMessage.from !== "user" && lastMessage.from !== "user")
      available = available - penultimateRef.offsetHeight - 24

    if (!containerRef.current || !userRef) return;

    spacerRef.current.style.minHeight = `${Math.max(0, available)}px`;

    userRef.scrollIntoView({
      behavior: "smooth",      // ou "auto"
      block: "start",          // aligne LE MESSAGE en haut du conteneur
      inline: "nearest"
    });
  }

  useLayoutEffect(() => {
    if ( firstLoad ) {
      setTimeout(() => {
        scrollToNeeded()
      }, 400)
    } else {
      scrollToNeeded()
    }
  }, [messages]);

  const scrollTop = () => {
    introRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  const scrollBottom = () => {
    const lastUser   = [...messages].reverse().find((m) => m.from === "user");
    const targetNode = itemRefs.current[lastUser.id].el;
    targetNode.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 relative overflow-y-auto no-scrollbar
      max-w-3xl w-full mx-auto d-vh mb-4 px-6
      flex flex-col gap-6 max-h-screen"
    >
      {(<div className="flex items-center flex-col" ref={introRef}>
        {/* Avatar */}
        {expert.avatar && (
          chatId !== 'lux' && chatId !== 'dip' && chatId !== 'sup' ? (<img
            onClick={gotoJobPage}
            src={expert.avatar}
            alt={expert.name}
            className="w-16 h-16 rounded-full object-cover mb-4 cursor-pointer"
          />) : (
          <video
              className="w-16 h-full object-cover rounded-full"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={expert.video} type="video/mp4" />
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>
            //   <img
            //   src={expert.avatar}
            //   alt={expert.name}
            //   className="w-16 h-16 rounded-full object-cover mb-4"
            // />
          )
        )}
        {/* Name */}
        {chatId !== 'lux' && chatId !== 'dip' && chatId !== 'sup' && (<h1 className="text-2xl font-semibold mb-2">{expert.name} - {expert.job}</h1>)}
        {(chatId === 'dip' || chatId === 'sup') && (<h1 className="text-2xl font-semibold mb-2 mt-2">{expert.name}</h1>)}
        {(chatId === 'dip' || chatId === 'sup') && (<p className="text-zinc-400 text-sm text-center max-w-md mb-2">{expert.bio}</p>)}

        {/* Suggestions */}
        {messages.length === 0 && expert.suggestions?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6 justify-center">
            {expert.suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-zinc-300 transition"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>)}
      {messages.map((m) => (
        <div
            key={m.id}
            ref={(el) => (itemRefs.current[m.id] = { el: el, from: m.from })}
            className={
              "whitespace-pre-line break-words leading-relaxed " +
              (m.from === "user"
                ? "self-end  bg-gray-800 px-6 py-3 rounded-2xl max-w-[80%] text-left"
                : "self-start text-base text-left")
            }
          >
          { m.type == "text" && (m.from === "user" ? m.content : <HumanTyping firstLoad={firstLoad} text={m.content} sentenceDone={sentenceDone} />)}
        </div>
        
      ))}

      {isAwaitingResponse &&
        <div className="message recieved pt-5">
            <TypingIndicator />
        </div>}
      <div ref={spacerRef} aria-hidden />
      <div className="fixed left-1/2 bottom-[85px]"
          style={{transform: "translateX(calc(-50% + var(--sidebar-w) / 2))"}}
      >
      {zone === "BOTTOM" && (
        <button onClick={scrollTop}
          className="rounded-full p-2 hover:bg-[#2C3E50]/10"
        >
          <ArrowUpCircle size={25} strokeWidth={1} />
        </button>
      )}
      {zone === "TOP" && (
        <button onClick={scrollBottom}
          className="rounded-full p-2 hover:bg-[#2C3E50]/10"
        >
          <ArrowDownCircle size={25} strokeWidth={1} />
        </button>
      )}
      </div>
    </div>
  );
}
